/* global allViolations */
import { teamMap } from 'bam-node-baseball-savant/constants/teams'
import * as d3 from 'd3'

const data = allViolations

let isWide = window.innerWidth > 1023
const marginLong = { top: 120, right: 50, bottom: 100, left: 50 }
const marginWide = { top: 80, right: 50, bottom: 80, left: 50 }

let margin = isWide ? marginWide : marginLong

const svg = d3.select('#viz-container svg')

const g = svg.append('g')

const teamMapById = Object.values(teamMap).reduce((acc, team) => {
    acc[team.abbrev] = team.id
    return acc
}, {})

const teamArrangement = [
    //
    'BAL',
    'BOS',
    'NYY',
    'TB',
    'TOR',
    //
    'CWS',
    'CLE',
    'DET',
    'KC',
    'MIN',
    //
    'ATH',
    'HOU',
    'LAA',
    'SEA',
    'TEX',
    //
    'ATL',
    'MIA',
    'NYM',
    'PHI',
    'WSH',
    //
    'CHC',
    'CIN',
    'MIL',
    'PIT',
    'STL',
    //
    'AZ',
    'COL',
    'LAD',
    'SD',
    'SF',
]

const teamArrangementById = teamArrangement.map((d) => Number(teamMapById[d]))

const fieldingTeamViolations = [
    'catcher_pitch_timer',
    'pitcher_pitch_timer',
    'defensive_shift',
]

const formattedData = data
    .map((d) => {
        const awayName =
            d.away_name_abbrev.length === 3
                ? d.away_name_abbrev
                : `&nbsp;${d.away_name_abbrev}`
        const homeName =
            d.home_name_abbrev.length === 3
                ? d.home_name_abbrev
                : `${d.home_name_abbrev}&nbsp;`

        const game = `${awayName} @ ${homeName}`

        const violating_team_id = fieldingTeamViolations.includes(
            d.pitch_violation_code
        )
            ? d.fld_team_id
            : d.bat_team_id

        const violating_team_name = fieldingTeamViolations.includes(
            d.pitch_violation_code
        )
            ? d.fld_name_abbrev
            : d.bat_name_abbrev

        const violated_team_id = fieldingTeamViolations.includes(
            d.pitch_violation_code
        )
            ? d.bat_team_id
            : d.fld_team_id

        const violated_team_name = fieldingTeamViolations.includes(
            d.pitch_violation_code
        )
            ? d.bat_name_abbrev
            : d.fld_name_abbrev

        const count = `${d.pre_ball_count}-${d.pre_strike_count}`

        const is_strikeout = d.pre_strike_count === '2' && d.is_ball === '0'
        const is_walk = d.pre_ball_count === '3' && d.is_ball === '1'

        const is_ab = is_strikeout || is_walk

        let type = 'strike'
        if (is_strikeout) {
            type = 'strikeout'
        } else if (is_walk) {
            type = 'walk'
        } else if (d.is_ball === '1') {
            type = 'ball'
        }

        return {
            ...d,
            type,
            game,
            violated_team_id,
            violated_team_name,
            violating_team_id,
            violating_team_name,
            count,
            is_ab,
            is_walk,
            is_strikeout,
        }
    })
    .sort((a, b) => (a.is_ball < b.is_ball ? 1 : -1))

const dataMappedByTeam = Object.keys(teamMap).reduce((acc, id) => {
    acc[id] = {
        violating: { walk: 0, strikeout: 0, ball: 0, strike: 0 },
        violated: { walk: 0, strikeout: 0, ball: 0, strike: 0 },
    }
    return acc
}, {})

formattedData.forEach((d) => {
    dataMappedByTeam[d.violating_team_id].violating[d.type] += 1

    dataMappedByTeam[d.violated_team_id].violated[d.type] += 1
})

const max = d3.max(
    Object.values(dataMappedByTeam),
    ({ violated, violating }) => {
        const violatedValue = d3.sum(Object.values(violated))
        const violatingValue = d3.sum(Object.values(violating))
        return Math.max(violatedValue, violatingValue)
    }
)

const niceMax = max < 50 ? Math.ceil(max / 5) * 5 : Math.ceil(max / 10) * 10

const ballStrikeColorScale = d3
    .scaleOrdinal()
    .domain(['ball', 'strike'])
    .range(d3.schemeTableau10)

svg.select('#diagonal-hatches-ball #background-ball').style(
    'stroke',
    ballStrikeColorScale('ball')
)
svg.select('#diagonal-hatches-strike #background-strike').style(
    'stroke',
    ballStrikeColorScale('strike')
)

const title = svg.append('text').attr('class', 'title')

function addTitle(totalWidth) {
    title
        .attr('x', totalWidth / 2)
        .attr('y', 28)
        .attr('font-size', '20px')
        .attr('font-weight', 'bold')
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .text('Pitch Timer Infractions By Team')
}

function addLegend() {
    // Lazy
    svg.selectAll('g.legend').remove()

    svg.selectAll('g.legend')
        .data(['Automatic Ball', 'Walk', 'Automatic Strike', 'Strikeout'])
        .join((enter) => {
            const legendItem = enter
                .append('g')
                .attr('class', 'legend')
                .attr('transform', (d, i) => {
                    if (isWide) {
                        return `translate(${margin.left + 130 * (i % 2)}, ${
                            Math.floor(i / 2) * 18 + 28
                        })`
                    }
                    return `translate(${margin.left + i * 120}, ${54})`
                })
            legendItem
                .append('rect')
                .attr('fill', (d) => {
                    switch (d) {
                        case 'Walk':
                            return 'url(#diagonal-hatches-ball)'
                        case 'Strikeout':
                            return 'url(#diagonal-hatches-strike)'
                        case 'Automatic Strike':
                            return ballStrikeColorScale('strike')
                        case 'Automatic Ball':
                        default:
                            return ballStrikeColorScale('ball')
                    }
                })
                .attr('width', 12)
                .attr('height', 12)
                .attr('y', -6)
                .attr('x', 6)

            legendItem
                .append('text')
                .text((d) => d)
                .attr('x', 20)
                .attr('dominant-baseline', 'middle')

            return legendItem
        })
}

function sortData(sort) {
    const entries = Object.entries(dataMappedByTeam)
    switch (sort) {
        case 'division':
            return entries.sort(([keyA], [keyB]) => {
                const teamA = teamArrangementById.indexOf(Number(keyA))
                const teamB = teamArrangementById.indexOf(Number(keyB))
                return teamA > teamB ? 1 : -1
            })
        case 'net':
            return entries.sort(([, valuesA], [, valuesB]) => {
                const teamAFor = d3.sum(Object.values(valuesA.violated))
                const teamBFor = d3.sum(Object.values(valuesB.violated))
                const teamAAgainst = d3.sum(Object.values(valuesA.violating))
                const teamBAgainst = d3.sum(Object.values(valuesB.violating))

                if (teamAFor - teamAAgainst !== teamBFor - teamBAgainst) {
                    return teamAFor - teamAAgainst > teamBFor - teamBAgainst
                        ? -1
                        : 1
                }

                return teamAFor < teamBFor ? 1 : -1
            })
        case 'ballsAgainst':
            return entries.sort(([, valuesA], [, valuesB]) => {
                const {
                    ball: ballA,
                    walk: walkA,
                    strike: strikeA,
                    strikeout: strikeoutA,
                } = valuesA.violating
                const {
                    ball: ballB,
                    walk: walkB,
                    strike: strikeB,
                    strikeout: strikeoutB,
                } = valuesB.violating

                const teamAAgainst = ballA + walkA
                const teamBAgainst = ballB + walkB

                if (teamAAgainst !== teamBAgainst) {
                    return teamAAgainst > teamBAgainst ? 1 : -1
                }

                if (walkA !== walkB) {
                    return walkA > walkB ? 1 : -1
                }

                const teamAAgainstStrikes = strikeA + strikeoutA
                const teamBAgainstStrikes = strikeB + strikeoutB

                if (teamAAgainstStrikes !== teamBAgainstStrikes) {
                    return teamAAgainstStrikes > teamBAgainstStrikes ? 1 : -1
                }

                if (strikeoutA !== strikeoutB) {
                    return strikeoutA > strikeoutB ? 1 : -1
                }

                return -1
            })
        case 'strikesAgainst':
            return entries.sort(([, valuesA], [, valuesB]) => {
                const {
                    ball: ballA,
                    walk: walkA,
                    strike: strikeA,
                    strikeout: strikeoutA,
                } = valuesA.violating
                const {
                    ball: ballB,
                    walk: walkB,
                    strike: strikeB,
                    strikeout: strikeoutB,
                } = valuesB.violating

                const teamAAgainstStrikes = strikeA + strikeoutA
                const teamBAgainstStrikes = strikeB + strikeoutB

                if (teamAAgainstStrikes !== teamBAgainstStrikes) {
                    return teamAAgainstStrikes > teamBAgainstStrikes ? 1 : -1
                }

                if (strikeoutA !== strikeoutB) {
                    return strikeoutA > strikeoutB ? 1 : -1
                }

                const teamAAgainst = ballA + walkA
                const teamBAgainst = ballB + walkB

                if (teamAAgainst !== teamBAgainst) {
                    return teamAAgainst > teamBAgainst ? 1 : -1
                }

                if (walkA !== walkB) {
                    return walkA > walkB ? 1 : -1
                }

                return -1
            })
        case 'ballsFor':
            return entries.sort(([, valuesA], [, valuesB]) => {
                const {
                    ball: ballA,
                    walk: walkA,
                    strike: strikeA,
                    strikeout: strikeoutA,
                } = valuesA.violated
                const {
                    ball: ballB,
                    walk: walkB,
                    strike: strikeB,
                    strikeout: strikeoutB,
                } = valuesB.violated

                const teamAAgainst = ballA + walkA
                const teamBAgainst = ballB + walkB

                if (teamAAgainst !== teamBAgainst) {
                    return teamAAgainst < teamBAgainst ? 1 : -1
                }

                if (walkA !== walkB) {
                    return walkA < walkB ? 1 : -1
                }

                const teamAAgainstStrikes = strikeA + strikeoutA
                const teamBAgainstStrikes = strikeB + strikeoutB

                if (teamAAgainstStrikes !== teamBAgainstStrikes) {
                    return teamAAgainstStrikes < teamBAgainstStrikes ? 1 : -1
                }

                if (strikeoutA !== strikeoutB) {
                    return strikeoutA < strikeoutB ? 1 : -1
                }
                return -1
            })

        case 'strikesFor':
            return entries.sort(([, valuesA], [, valuesB]) => {
                const {
                    ball: ballA,
                    walk: walkA,
                    strike: strikeA,
                    strikeout: strikeoutA,
                } = valuesA.violated
                const {
                    ball: ballB,
                    walk: walkB,
                    strike: strikeB,
                    strikeout: strikeoutB,
                } = valuesB.violated

                const teamAAgainstStrikes = strikeA + strikeoutA
                const teamBAgainstStrikes = strikeB + strikeoutB

                if (teamAAgainstStrikes !== teamBAgainstStrikes) {
                    return teamAAgainstStrikes < teamBAgainstStrikes ? 1 : -1
                }

                if (strikeoutA !== strikeoutB) {
                    return strikeoutA < strikeoutB ? 1 : -1
                }

                const teamAAgainst = ballA + walkA
                const teamBAgainst = ballB + walkB

                if (teamAAgainst !== teamBAgainst) {
                    return teamAAgainst < teamBAgainst ? 1 : -1
                }

                if (walkA !== walkB) {
                    return walkA < walkB ? 1 : -1
                }

                return -1
            })
        case 'violated':
            return entries.sort(([, valuesA], [, valuesB]) => {
                const teamA = d3.sum(Object.values(valuesA.violated))
                const teamB = d3.sum(Object.values(valuesB.violated))
                return teamA > teamB ? -1 : 1
            })
        case 'violating':
        default:
            return entries.sort(([, valuesA], [, valuesB]) => {
                const teamA = d3.sum(Object.values(valuesA.violating))
                const teamB = d3.sum(Object.values(valuesB.violating))
                return teamA > teamB ? 1 : -1
            })
    }
}

function drawChart(sort) {
    const totalWidth = isWide ? 1024 : Math.min(window.innerWidth, 600)
    const totalHeight = isWide ? 600 : 1000

    addTitle(totalWidth)
    addLegend()

    const height = totalHeight - margin.top - margin.bottom
    const width = totalWidth - margin.left - margin.right

    const longDir = isWide ? width : height

    svg.attr('width', totalWidth).attr('height', totalHeight)

    g.attr('transform', `translate(${margin.left}, ${margin.top})`)
    const imageSize = 20
    const padding = (longDir - imageSize * 30) / 29

    const scaleMax = isWide ? height : width

    const barScale = d3
        .scaleLinear()
        .domain([0, niceMax])
        .range([0, scaleMax / 2 - imageSize - padding])

    const barNudge = imageSize / 2 + padding

    const posProp = isWide ? 'violated' : 'violating'
    const negProp = isWide ? 'violating' : 'violated'

    const barHeightAttr = isWide ? 'height' : 'width'
    const barWidthAttr = isWide ? 'width' : 'height'

    const nudgeAttr = isWide ? 'y' : 'x'
    const nonNudgeAttr = isWide ? 'x' : 'y'

    const offsetAttr = isWide ? 'y' : 'x'
    const nonOffsetAttr = isWide ? 'x' : 'y'

    const ballFirst = !['strikesFor', 'strikesAgainst'].includes(sort)
    const prop1 = ballFirst ? 'walk' : 'strikeout'
    const prop2 = ballFirst ? 'ball' : 'strike'
    const prop3 = ballFirst ? 'strike' : 'ball'
    const prop4 = ballFirst ? 'strikeout' : 'walk'

    drawAxes(barScale, barNudge, width, height)

    const chartData = sortData(sort)

    g.selectAll('g.team')
        .data(chartData, ([key]) => key)
        .join(
            (enter) => {
                const el = enter
                    .append('g')
                    .attr('class', 'team')
                    .attr('transform', (_, i) => {
                        const x = isWide
                            ? i * (imageSize + padding)
                            : scaleMax / 2
                        const y = isWide
                            ? scaleMax / 2
                            : i * (imageSize + padding)
                        return `translate(${x}, ${y})`
                    })

                el.append('image')
                    .attr(
                        'href',
                        ([key]) =>
                            `https://www.mlbstatic.com/team-logos/${key}.svg`
                    )
                    .attr('width', imageSize)
                    .attr('height', imageSize)
                    .attr(nudgeAttr, -imageSize / 2)

                el.append('rect')
                    .attr('class', `${posProp} ${posProp}-${prop1} ${prop1}`)
                    .attr(barHeightAttr, ([, d]) => barScale(d[posProp][prop1]))
                    .attr(barWidthAttr, imageSize)
                    .attr(offsetAttr, barNudge)
                    .attr('fill', `url(#diagonal-hatches-${prop2})`)

                el.append('rect')
                    .attr('class', `${posProp} ${posProp}-${prop2} ${prop2}`)
                    .attr(barHeightAttr, ([, d]) => barScale(d[posProp][prop2]))
                    .attr(barWidthAttr, imageSize)
                    .attr(
                        offsetAttr,
                        ([, d]) => barNudge + barScale(d[posProp][prop1])
                    )
                    .attr('fill', ballStrikeColorScale(prop2))

                el.append('rect')
                    .attr('class', `${posProp} ${posProp}-${prop3} ${prop3}`)
                    .attr(barHeightAttr, ([, d]) => barScale(d[posProp][prop3]))
                    .attr(barWidthAttr, imageSize)
                    .attr(
                        offsetAttr,
                        ([, d]) =>
                            barNudge +
                            barScale(d[posProp][prop1] + d[posProp][prop2])
                    )
                    .attr('fill', ballStrikeColorScale(prop3))

                el.append('rect')
                    .attr('class', `${posProp} ${posProp}-${prop4} ${prop4}`)
                    .attr(barHeightAttr, ([, d]) => barScale(d[posProp][prop4]))
                    .attr(barWidthAttr, imageSize)
                    .attr(
                        offsetAttr,
                        ([, d]) =>
                            barNudge +
                            barScale(
                                d[posProp][prop1] +
                                    d[posProp][prop2] +
                                    d[posProp][prop3]
                            )
                    )
                    .attr('fill', `url(#diagonal-hatches-${prop3})`)

                el.append('rect')
                    .attr('class', `${negProp} ${negProp}-${prop1} ${prop1}`)
                    .attr(barHeightAttr, ([, d]) => barScale(d[negProp][prop1]))
                    .attr(barWidthAttr, imageSize)
                    .attr(
                        offsetAttr,
                        ([, d]) => -barScale(d[negProp][prop1]) - barNudge
                    )
                    .attr('fill', `url(#diagonal-hatches-${prop2})`)

                el.append('rect')
                    .attr('class', `${negProp} ${negProp}-${prop2} ${prop2}`)
                    .attr(barHeightAttr, ([, d]) => barScale(d[negProp][prop2]))
                    .attr(barWidthAttr, imageSize)
                    .attr(
                        offsetAttr,
                        ([, d]) =>
                            -barScale(d[negProp][prop1] + d[negProp][prop2]) -
                            barNudge
                    )
                    .attr('fill', ballStrikeColorScale(prop2))

                el.append('rect')
                    .attr('class', `${negProp} ${negProp}-${prop3} ${prop3}`)
                    .attr(barHeightAttr, ([, d]) => barScale(d[negProp][prop3]))
                    .attr(barWidthAttr, imageSize)
                    .attr(
                        offsetAttr,
                        ([, d]) =>
                            -barScale(
                                d[negProp][prop1] +
                                    d[negProp][prop2] +
                                    d[negProp][prop3]
                            ) - barNudge
                    )
                    .attr('fill', ballStrikeColorScale(prop3))

                el.append('rect')
                    .attr('class', `${negProp} ${negProp}-${prop4} ${prop4}`)
                    .attr(barHeightAttr, ([, d]) => barScale(d[negProp][prop4]))
                    .attr(barWidthAttr, imageSize)
                    .attr(
                        offsetAttr,
                        ([, d]) =>
                            -barScale(
                                d[negProp][prop1] +
                                    d[negProp][prop2] +
                                    d[negProp][prop3] +
                                    d[negProp][prop4]
                            ) - barNudge
                    )
                    .attr('fill', `url(#diagonal-hatches-${prop3})`)

                return el
            },
            (update) => {
                update.transition().attr('transform', (_, i) => {
                    const x = isWide ? i * (imageSize + padding) : scaleMax / 2
                    const y = isWide ? scaleMax / 2 : i * (imageSize + padding)
                    return `translate(${x}, ${y})`
                })

                update
                    .select('image')
                    .transition()
                    .attr('width', imageSize)
                    .attr('height', imageSize)
                    .attr(nudgeAttr, -imageSize / 2)
                    .attr(nonNudgeAttr, 0)

                update
                    .select(`.${posProp}-${prop1}`)
                    .transition()
                    .attr(barHeightAttr, ([, d]) => barScale(d[posProp][prop1]))
                    .attr(barWidthAttr, imageSize)
                    .attr(offsetAttr, barNudge)
                    .attr(nonOffsetAttr, 0)

                update
                    .select(`.${posProp}-${prop2}`)
                    .transition()
                    .attr(barHeightAttr, ([, d]) => barScale(d[posProp][prop2]))
                    .attr(barWidthAttr, imageSize)
                    .attr(
                        offsetAttr,
                        ([, d]) => barNudge + barScale(d[posProp][prop1])
                    )
                    .attr(nonOffsetAttr, 0)

                update
                    .select(`.${posProp}-${prop3}`)
                    .transition()

                    .attr(barHeightAttr, ([, d]) => barScale(d[posProp][prop3]))
                    .attr(barWidthAttr, imageSize)
                    .attr(
                        offsetAttr,
                        ([, d]) =>
                            barNudge +
                            barScale(d[posProp][prop1] + d[posProp][prop2])
                    )
                    .attr(nonOffsetAttr, 0)

                update
                    .select(`.${posProp}-${prop4}`)
                    .transition()
                    .attr(barHeightAttr, ([, d]) => barScale(d[posProp][prop4]))
                    .attr(barWidthAttr, imageSize)
                    .attr(
                        offsetAttr,
                        ([, d]) =>
                            barNudge +
                            barScale(
                                d[posProp][prop1] +
                                    d[posProp][prop2] +
                                    d[posProp][prop3]
                            )
                    )
                    .attr(nonOffsetAttr, 0)

                update
                    .select(`.${negProp}-${prop1}`)
                    .transition()
                    .attr(barHeightAttr, ([, d]) => barScale(d[negProp][prop1]))
                    .attr(barWidthAttr, imageSize)
                    .attr(
                        offsetAttr,
                        ([, d]) => -barScale(d[negProp][prop1]) - barNudge
                    )
                    .attr(nonOffsetAttr, 0)

                update
                    .select(`.${negProp}-${prop2}`)
                    .transition()
                    .attr(barHeightAttr, ([, d]) => barScale(d[negProp][prop2]))
                    .attr(barWidthAttr, imageSize)
                    .attr(
                        offsetAttr,
                        ([, d]) =>
                            -barScale(d[negProp][prop1] + d[negProp][prop2]) -
                            barNudge
                    )
                    .attr(nonOffsetAttr, 0)

                update
                    .select(`.${negProp}-${prop3}`)
                    .transition()
                    .attr(barHeightAttr, ([, d]) => barScale(d[negProp][prop3]))
                    .attr(barWidthAttr, imageSize)
                    .attr(
                        offsetAttr,
                        ([, d]) =>
                            -barScale(
                                d[negProp][prop1] +
                                    d[negProp][prop2] +
                                    d[negProp][prop3]
                            ) - barNudge
                    )
                    .attr(nonOffsetAttr, 0)

                update
                    .select(`.${negProp}-${prop4}`)
                    .transition()
                    .attr(barHeightAttr, ([, d]) => barScale(d[negProp][prop4]))
                    .attr(barWidthAttr, imageSize)
                    .attr(
                        offsetAttr,
                        ([, d]) =>
                            -barScale(
                                d[negProp][prop1] +
                                    d[negProp][prop2] +
                                    d[negProp][prop3] +
                                    d[negProp][prop4]
                            ) - barNudge
                    )
                    .attr(nonOffsetAttr, 0)

                return update
            }
        )
        .raise()
}

// These are misnamed for what it's worth
const benefitAxisOne = g.append('g').attr('class', 'benefit-axis-1 axis')
const benefitAxisTwo = g.append('g').attr('class', 'benefit-axis-2 axis')
const violatorAxisOne = g.append('g').attr('class', 'violator-axis-1 axis')
const violatorAxisTwo = g.append('g').attr('class', 'violator-axis-2 axis')

function drawAxes(scale, padding, width, height) {
    const [, maxRange] = scale.range()

    const nudge = 8

    const x1 = isWide ? -nudge : width / 2 - maxRange - padding
    const y1 = isWide ? height / 2 - maxRange - padding : -nudge

    const x2 = isWide ? -nudge : width / 2 + padding
    const y2 = isWide ? height / 2 + padding : -nudge

    const x3 = isWide ? width + nudge : width / 2 - maxRange - padding
    const y3 = isWide ? height / 2 - maxRange - padding : height + nudge

    const x4 = isWide ? width + nudge : width / 2 + padding
    const y4 = isWide ? height / 2 + padding : height + nudge

    const axisTypeA = isWide ? 'axisLeft' : 'axisTop'
    const axisTypeB = isWide ? 'axisRight' : 'axisBottom'

    const otherScale = d3
        .scaleLinear()
        .domain(scale.domain())
        .range([maxRange, 0])

    const ticks = 4
    const tickLines = d3[axisTypeA](otherScale)
        .scale()
        .ticks(ticks)
        // Ditch 0
        .filter((d) => d)

    const line = d3.line()

    g.selectAll('path.axis-line').remove()

    g.selectAll('path.axis-line-one')
        .data(tickLines)
        .join((enter) =>
            enter
                .append('path')
                .attr('class', 'axis-line-one axis-line')
                .attr('stroke', '#c2c2c2')
                .attr('stroke-width', 1)
                .attr('stroke-dasharray', '5,5')
                .attr('d', (d) => {
                    if (isWide) {
                        const y = y1 + otherScale(d)
                        return line([
                            [0, y],
                            [width, y],
                        ])
                    }
                    const x = x1 + otherScale(d)
                    return line([
                        [x, 0],
                        [x, height],
                    ])
                })
        )
    g.selectAll('path.axis-line-two')
        .data(tickLines)
        .join((enter) =>
            enter
                .append('path')
                .attr('class', 'axis-line-two axis-line')
                .attr('stroke', '#c2c2c2')
                .attr('stroke-width', 1)
                .attr('stroke-dasharray', '5,5')
                .attr('d', (d) => {
                    if (isWide) {
                        const y = y2 + scale(d)
                        return line([
                            [0, y],
                            [width, y],
                        ])
                    }

                    const x = x2 + scale(d)
                    return line([
                        [x, 0],
                        [x, height],
                    ])
                })
        )

    // Meh
    d3.selectAll('.axis').selectAll('*').remove()

    benefitAxisOne
        .attr('transform', `translate(${x1}, ${y1})`)
        .call(d3[axisTypeA](otherScale).ticks(ticks))

    benefitAxisTwo
        .attr('transform', `translate(${x2}, ${y2})`)
        .call(d3[axisTypeA](scale).ticks(ticks))

    violatorAxisOne
        .attr('transform', `translate(${x3}, ${y3})`)
        .call(d3[axisTypeB](otherScale).ticks(ticks))

    violatorAxisTwo
        .attr('transform', `translate(${x4}, ${y4})`)
        .call(d3[axisTypeB](scale).ticks(ticks))

    // draw labels
    const labelPadding = 32

    g.selectAll('.axis-label').remove()

    g.append('text')
        .attr('class', 'axis-label-one axis-label')
        .text('Infractions Benefitting Team')
        .attr('font-size', 12)
        .attr('transform', () => {
            if (isWide) {
                return `translate(${-labelPadding}, ${
                    height / 2 + padding + maxRange / 2
                }) rotate(-90)`
            }
            return `translate(${padding / 2 + maxRange / 2}, ${-labelPadding})`
        })
        .attr('text-anchor', 'middle')

    g.append('text')
        .attr('class', 'axis-label-one axis-label')
        .text('Infractions Against Team')
        .attr('font-size', 12)
        .attr('transform', () => {
            if (isWide) {
                // y value isn't quite right. Meh
                return `translate(${-labelPadding}, ${
                    maxRange / 2
                }) rotate(-90)`
            }
            return `translate(${
                width / 2 + padding + maxRange / 2
            }, ${-labelPadding})`
        })
        .attr('text-anchor', 'middle')
}

drawChart('violating')

window.addEventListener('resize', () => {
    const newIsWide = window.innerWidth > 1023
    if (isWide !== newIsWide) {
        isWide = newIsWide
        margin = isWide ? marginWide : marginLong
        drawChart()
    }
})

document.getElementById('ddlViolationSort').addEventListener('change', (e) => {
    drawChart(e.target.value)
})
